import {Fragment, useEffect, useMemo, useState} from "react";
import {connect} from "react-redux";
import {css, Theme} from "@emotion/react";
import styled from "@emotion/styled";
import {bindActionCreators, Dispatch} from "redux";

import {Button} from "@pg-design/button-module";
import {WideContent} from "@pg-design/grid-module";
import {backgroundColor, calculateRemSize, flex, flexDirection, mb, mt, p, pb, ph, pv, w100} from "@pg-design/helpers-css";
import {ArrowIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, FiltersIcon} from "@pg-design/icons-module";
import {InputWrapper} from "@pg-design/inputs-module";
import {Loader} from "@pg-design/loader-module";
import {PaginationSize, PaginationWithList} from "@pg-design/pagination";
import {getThemeBreakpoint} from "@pg-design/styles-strings";
import {Text} from "@pg-design/text-module";
import {FormActions} from "@pg-mono/form";
import {uniqBy} from "@pg-mono/nodash";
import {useUserDevice} from "@pg-mono/user-device";

import {scrollToTop} from "../../app/components/ScrollToTop";
import {IRPStore} from "../../app/rp_reducer";
import {ListingTypeTabs} from "../../atoms/listing_type_tabs/ListingTypeTabs";
import {MobileOptionsModal} from "../../atoms/MobileOptionsModal";
import {notifyBugsnag} from "../../errors/bugsnag/notify_bugsnag";
import {ErrorView} from "../../errors/views/ErrorView";
import {LeadModal} from "../../lead/components/lead_modal/LeadModal";
import {sortWrapper} from "../../offer/detail/components/property_list/OfferDetailPropertyListFilters";
import {Sort} from "../../offer/detail/components/sort/Sort";
import {SortOptions} from "../../offer/detail/components/sort/SortOptions";
import {sortOptions} from "../../offer/list/components/OfferListSort";
import {OfferType} from "../../offer/types/OfferType";
import {propertyListSortOptions} from "../../property/constants/property_list_sort_options";
import {IPropertyListProperty} from "../../property/types/PropertyListProperty";
import {fetchAllSearchLists, IFetchAllSearchListsParams, SearchTab} from "../../search/actions/fetch_search_all_action";
import {ListingType} from "../../types/ListingType";
import {IValueLabel} from "../../variables/types/IValueLabel";
import {GTMEvent, propertyConfiguratorAnalytics} from "../analytics/events";
import {hitPropertyConfiguratorSearch} from "../analytics/hit_property_configurator_search";
import {fetchAllPropertiesCount} from "../api/fetch_all_properties_count";
import {fetchAllPropertiesFromOffer} from "../api/fetch_all_properties_from_offer";
import {fetchFirstProperties} from "../api/fetch_first_properties";
import {FiltersForm} from "../components/filters/FiltersForm";
import {MobileFiltersModal} from "../components/filters/MobileFiltersModal";
import {HorizontalPropertyBox} from "../components/HorizontalPropertyBox";
import {Map} from "../components/Map";
import {OfferHeader} from "../components/OfferHeader";
import {usePropertyConfiguratorFiltersAnalytics} from "../hooks/use_property_configurator_filters_analytics";
import {searchFormFields} from "../reducers/property_configurator_reducer";
import {IPropertyConfiguratorProperty} from "../types/IPropertyConfiguratorProperty";
import {getParams} from "../utils/get_property_api_params";
import {getValuesWithCurrentSearchFieldState} from "../utils/get_values_with_current_search_field_state";
import {handleFormChange} from "../utils/handle_form_change";
import {getPropertyConfiguratorStorageData} from "../utils/property_configurator_storage";
import {searchFormActions, SearchFormValuesType} from "./PropertyConfigurator";

interface IStateProps {
    formValues: SearchFormValuesType;
    propertyQuarters: IValueLabel[] | undefined;
}
interface IActionsProps {
    formActions: FormActions<Record<keyof typeof searchFormFields, unknown>>;
    fetchAllSearchLists: (searchInput: string, params: IFetchAllSearchListsParams) => Promise<SearchTab>;
}

interface IOwnProps {
    handleStoreValues: (values: SearchFormValuesType) => void;
}

interface IProps extends IStateProps, IActionsProps, IOwnProps {}

export const ResultsListC = (props: IProps) => {
    const [isDropdownOpen, setDropdownOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [currentOfferPage, setCurrentOfferPage] = useState(1);
    const [maxPage, setMaxPage] = useState(1);
    const [maxOfferPage, setMaxOfferPage] = useState(1);
    const {isMobile} = useUserDevice();
    const [isLoading, setIsLoading] = useState(false);
    const [fetchingError, setFetchingError] = useState(false);
    const [firstProperties, setFirstProperties] = useState<IPropertyConfiguratorProperty[]>([]);
    const [allPropertiesCount, setAllPropertiesCount] = useState(0);
    const [propertiesFromOffer, setPropertiesFromOffer] = useState<IPropertyConfiguratorProperty[]>([]);
    const [propertiesInOfferCount, setPropertiesInOfferCount] = useState(0);
    const [sortValue, setSortValue] = useState(1);
    const [temporarySortValue, setTemporarySortValue] = useState(0);
    const [propertySort, setPropertySort] = useState(0);
    const [isMobileSortActive, setMobileSortActive] = useState(false);
    const [firstPropertiesParamsJSON, setFirstPropertiesParamsJSON] = useState("");
    const [filtersModalOpen, setFiltersModalOpen] = useState(false);
    const [_, setShouldRerender] = useState(0);

    const {trackArea, trackFloors, trackRooms, trackWorldDirections, trackAdditionalAreas, trackKitchenType} = usePropertyConfiguratorFiltersAnalytics(
        GTMEvent.FLAT_CONFIG_SIDEBAR
    );

    const filterProps = {
        values: props.formValues,
        onChange: (value: Partial<SearchFormValuesType>) => {
            handleFormChange(value, props.formActions.update, props.formValues);

            trackRooms(value);
        }
    };

    const [expandedOffer, setExpandedOffer] = useState<null | IPropertyListProperty>(null);

    const propertiesWithUniqueSlugs = uniqBy(firstProperties, (item) => item.offer.slug);
    const sortOptionsVariant = useMemo(
        () => (expandedOffer ? propertyListSortOptions : sortOptions.map((option) => ({label: option.title, value: parseInt(option.value)}))),
        [expandedOffer]
    );

    const getFirstProperties = async (page: number, name?: string, value?: unknown) => {
        try {
            const values = {
                ...props.formValues,
                ...(name === "search" && {search: value}),
                ...(name === "price" && {price: value}),
                ...(name === "construction_end_date" && {construction_end_date: value})
            };
            const params = getParams(values, sortValue);
            const paramsWithPage = {...params, page};
            if (JSON.stringify(paramsWithPage) === firstPropertiesParamsJSON) {
                return;
            }
            scrollToTop();
            setFetchingError(false);
            setIsLoading(true);
            setFirstPropertiesParamsJSON(JSON.stringify(paramsWithPage));
            const data = await fetchFirstProperties(paramsWithPage);
            setFirstProperties(data.results);
            setMaxPage(Math.ceil(data.count / data.page_size));
            const newCount = await fetchAllPropertiesCount(params);
            hitPropertyConfiguratorSearch(values, data);
            setAllPropertiesCount(newCount);
            setIsLoading(false);
        } catch (error) {
            const message = "Error in fetchFirstProperties or fetchFirstPropertiesFlatsForSaleCount";
            setIsLoading(false);
            setFetchingError(true);
            notifyBugsnag(error, error instanceof Error ? error.message : message);
        }
    };

    const getPropertiesFromOffer = async (page: number, ignoreSort?: boolean) => {
        try {
            if (!expandedOffer) {
                return;
            }
            setFetchingError(false);
            setIsLoading(true);
            const propertySortedBy = (expandedOffer && propertyListSortOptions.find((option) => option.value === propertySort)?.slug) || (ignoreSort && "");
            const params = {
                ...getParams(getPropertyConfiguratorStorageData(), sortValue),
                page,
                ...(propertySortedBy ? {sort: propertySortedBy} : {})
            };
            const data = await fetchAllPropertiesFromOffer(params, expandedOffer.offer.id);
            setMaxOfferPage(Math.ceil(data.count / data.page_size));
            setPropertiesFromOffer(data.results);
            setPropertiesInOfferCount(data.count);
            setIsLoading(false);
            scrollToTop();
        } catch (error) {
            setIsLoading(false);
            setFetchingError(true);
        }
    };

    const handleOfferClick = (offerId: string | number) => {
        setIsLoading(true);
        setPropertiesFromOffer([]);
        const propertySortedBy = expandedOffer && propertyListSortOptions.find((option) => option.value === propertySort)?.slug;
        const params = {...getParams(props.formValues, sortValue), page: 1, ...(propertySortedBy ? {sort: propertySortedBy} : {})};
        const indexOfMatchingProperty = firstProperties.findIndex(({offer}) => offer.id === offerId);
        if (indexOfMatchingProperty >= 0) {
            setExpandedOffer(firstProperties[indexOfMatchingProperty]);
        }
        fetchAllPropertiesFromOffer(params, offerId).then((data) => {
            setCurrentOfferPage(1);
            setPropertiesFromOffer(data.results);
            setIsLoading(false);
            setPropertiesInOfferCount(data.count);
        });
    };

    const discardModal = () => {
        if (sortValue !== 0) {
            setSortValue(sortValue);
        }
        setMobileSortActive(false);
    };

    const clearSort = () => {
        if (expandedOffer) {
            setPropertySort(0);
            setTemporarySortValue(0);
        }
        if (!expandedOffer) {
            setSortValue(1);
            setTemporarySortValue(1);
        }
        if (isMobile) {
            setMobileSortActive(false);
        }
        setDropdownOpen(false);
    };

    const saveSort = () => {
        if (isMobile) {
            setMobileSortActive(false);
        }
        if (expandedOffer) {
            setPropertySort(temporarySortValue);
        } else {
            setSortValue(temporarySortValue);
        }
        setDropdownOpen(false);
    };

    useEffect(() => {
        setTimeout(() => getFirstProperties(currentPage), 10);
    }, [currentPage, sortValue]);

    useEffect(() => {
        getPropertiesFromOffer(currentOfferPage);
    }, [currentOfferPage, propertySort]);

    useEffect(() => {
        if (!expandedOffer) {
            setTemporarySortValue(sortValue);
            setMaxOfferPage(1);
            setCurrentOfferPage(1);
        }
        if (expandedOffer) {
            setTemporarySortValue(propertySort);
            getPropertiesFromOffer(1);
            setPropertiesFromOffer([]);
        }
    }, [expandedOffer]);

    useEffect(() => {
        if (!expandedOffer && sortValue !== temporarySortValue) {
            setTemporarySortValue(sortValue);
        }
        if (expandedOffer && propertySort !== temporarySortValue) {
            setTemporarySortValue(propertySort);
        }
    }, [isDropdownOpen]);

    useEffect(() => {
        //This has to be here in order to correctly render React-range's thumbs. Otherwise it tries to calculate thumbs' position when animation is started resulting in bad position. If we wait for modal to finish opening, then it's fine
        setTimeout(() => setShouldRerender((prev) => prev + 1), 250);
    }, [filtersModalOpen]);

    if (fetchingError) {
        return <ErrorView />;
    }

    return (
        <>
            <WideContent css={wrapper}>
                <div css={preferencesSidebar}>
                    <Text variant="headline_3">Twoje preferencje</Text>

                    <FiltersForm {...filterProps} />

                    <Button
                        disabled={isLoading}
                        size="big"
                        variant="filled_primary"
                        onClick={() => {
                            if (expandedOffer) {
                                setExpandedOffer(null);
                            }
                            if (currentPage !== 1) {
                                setCurrentPage(1);
                            } else {
                                getFirstProperties(1);
                            }
                            props.handleStoreValues(props.formValues);
                            propertyConfiguratorAnalytics.gtm.propertyConfiguratorEvent(GTMEvent.FLAT_CONFIG_CHANGE);
                            trackArea();
                            trackFloors();
                            trackWorldDirections();
                            trackAdditionalAreas();
                            trackKitchenType();
                        }}
                    >
                        Zapisz
                    </Button>
                </div>
                <div css={[flex(), flexDirection("column"), w100, pb(4)]}>
                    {expandedOffer ? (
                        <OfferHeader
                            handleBackClick={() => {
                                propertyConfiguratorAnalytics.gtm.propertyConfiguratorEvent(GTMEvent.FLAT_CONFIG_BACK);
                                setExpandedOffer(null);
                            }}
                            images={expandedOffer.offer?.main_image}
                            name={expandedOffer.offer.name}
                            address={expandedOffer.offer.address}
                            vendorLogo={expandedOffer.offer.vendor.logo?.v_log_160x120}
                            onChange={props.formActions.update}
                            values={props.formValues}
                            formActions={props.formActions}
                            onEditClick={setFiltersModalOpen}
                        />
                    ) : (
                        <Map
                            fetchAllSearchLists={props.fetchAllSearchLists}
                            onChange={props.formActions.update}
                            values={props.formValues}
                            formActions={props.formActions}
                            handleChange={(name, value) => {
                                propertyConfiguratorAnalytics.gtm.propertyConfiguratorEvent(GTMEvent.FLAT_CONFIG_CHANGE);
                                setCurrentPage(1);
                                getFirstProperties(1, name, value);
                                const values = getValuesWithCurrentSearchFieldState(props.formValues, name, value);
                                props.handleStoreValues(values);
                            }}
                            properties={firstProperties}
                            handleOfferClick={handleOfferClick}
                            onEditClick={setFiltersModalOpen}
                        />
                    )}
                    {isLoading && <Loader size="md" css={p(12.5)} />}
                    {!isLoading && (
                        <>
                            <div css={headerStyle}>
                                <Text variant="headline_4">
                                    {expandedOffer
                                        ? `Dostępne mieszkania w tej inwestycji (${propertiesInOfferCount})`
                                        : `Dostępne mieszkania w tej lokalizacji (${allPropertiesCount || 0})`}
                                </Text>
                                <HeaderFilterWrapper reverseDirection={!!expandedOffer}>
                                    {!expandedOffer && (
                                        <ListingTypeTabs
                                            filters={props.formValues}
                                            additionalParams={{sort: temporarySortValue}}
                                            currentListingType={ListingType.PROPERTIES}
                                            offerType={OfferType.FLAT}
                                        />
                                    )}
                                    <InputWrapper css={[sortWrapper, inputWrapper]}>
                                        <Sort
                                            isDropdownOpen={isDropdownOpen}
                                            setDropdownOpen={setDropdownOpen}
                                            isMobile={isMobile as boolean}
                                            setDesktopFilterBlendActive={() => null}
                                            currentValue={temporarySortValue}
                                            setCurrentValue={setTemporarySortValue}
                                            sortOptions={sortOptionsVariant}
                                            rightDropdown
                                            dropdownShadow
                                            disableClearAction
                                            closeOnOutsideClick
                                        >
                                            <SortOptions
                                                currentValue={temporarySortValue}
                                                setCurrentValue={setTemporarySortValue}
                                                sortOptions={sortOptionsVariant}
                                                onCancel={clearSort}
                                                onSave={saveSort}
                                            />
                                        </Sort>
                                    </InputWrapper>
                                </HeaderFilterWrapper>
                                {isMobile && (
                                    <>
                                        <Button
                                            css={[w100, mt(4)]}
                                            variant="outlined_secondary"
                                            iconLeft={FiltersIcon}
                                            onClick={() => setMobileSortActive((prev) => !prev)}
                                        >
                                            Sortuj
                                        </Button>
                                        <MobileOptionsModal modalHeading="Sortuj" isOpen={isMobileSortActive} onModalClose={discardModal} css={modalStyle}>
                                            <SortOptions
                                                currentValue={temporarySortValue}
                                                setCurrentValue={setTemporarySortValue}
                                                sortOptions={sortOptionsVariant}
                                                onCancel={clearSort}
                                                onSave={saveSort}
                                            />
                                        </MobileOptionsModal>
                                    </>
                                )}
                            </div>
                            <div css={resultsWrapper}>
                                {!expandedOffer &&
                                    propertiesWithUniqueSlugs.map((property, index) => (
                                        <Fragment key={index}>
                                            <HorizontalPropertyBox property={property} />
                                            {property.matching_properties_count > 1 && (
                                                <Button
                                                    variant="none_secondary"
                                                    onClick={() => {
                                                        setPropertiesFromOffer([]);
                                                        setExpandedOffer(property);
                                                        propertyConfiguratorAnalytics.gtm.propertyConfiguratorEvent(GTMEvent.FLAT_CONFIG_MORE);
                                                    }}
                                                    iconLeft={ChevronDownIcon}
                                                    css={bottomButton}
                                                >
                                                    Pokaż jeszcze {property.matching_properties_count - 1} z tej inwestycji
                                                </Button>
                                            )}
                                        </Fragment>
                                    ))}
                                {expandedOffer && propertiesFromOffer.map((property, index) => <HorizontalPropertyBox property={property} key={index} />)}
                            </div>
                            <PaginationWithList
                                iconPrev={<ChevronLeftIcon />}
                                iconNext={<ChevronRightIcon />}
                                pageCount={expandedOffer ? maxOfferPage : maxPage}
                                currentPage={expandedOffer ? currentOfferPage : currentPage}
                                multiNumbersInside
                                size={isMobile ? PaginationSize.SM : PaginationSize.LG}
                                onChangePageClick={expandedOffer ? setCurrentOfferPage : setCurrentPage}
                            />
                            {expandedOffer && (
                                <Button
                                    variant="outlined_secondary"
                                    iconLeft={ArrowIcon}
                                    onClick={() => {
                                        scrollToTop();
                                        propertyConfiguratorAnalytics.gtm.propertyConfiguratorEvent(GTMEvent.FLAT_CONFIG_BACK);
                                        setExpandedOffer(null);
                                    }}
                                    css={backToLocationButton}
                                >
                                    Powrót do lokalizacji
                                </Button>
                            )}
                        </>
                    )}
                </div>
                <LeadModal />
                <MobileFiltersModal
                    isOpen={filtersModalOpen}
                    onClose={setFiltersModalOpen}
                    handleChange={(name, value) => {
                        propertyConfiguratorAnalytics.gtm.propertyConfiguratorEvent(GTMEvent.FLAT_CONFIG_CHANGE);
                        setCurrentPage(1);
                        getFirstProperties(1, name, value);
                        const values = getValuesWithCurrentSearchFieldState(props.formValues, name, value);
                        props.handleStoreValues(values);
                    }}
                    {...filterProps}
                />
            </WideContent>
        </>
    );
};

export const ResultsList = connect(mapStateToProps, mapActionsToProps)(ResultsListC);

function mapActionsToProps(dispatch: Dispatch): IActionsProps {
    // fetch search lists for property configurator
    const searchContentParams = {
        googlePOIs: false,
        regions: true,
        offers: false,
        vendors: false
    };
    return {
        formActions: bindActionCreators({...searchFormActions}, dispatch),
        ...bindActionCreators({fetchAllSearchLists: (searchInput: string) => fetchAllSearchLists(searchInput, {searchContent: searchContentParams})}, dispatch)
    };
}

function mapStateToProps(state: IRPStore): IStateProps {
    return {
        formValues: state.propertyConfigurator.formValues,
        propertyQuarters: state.variables.data?.property_quarters
    };
}

const wrapper = css`
    ${flex()};
    gap: ${calculateRemSize(3)};
`;

const preferencesSidebar = (theme: Theme) => css`
    ${flex()};
    ${flexDirection("column")};
    width: 100%;
    max-width: 384px;
    ${backgroundColor("white")};
    ${ph(3)};
    ${pv(4)};
    gap: ${calculateRemSize(4)};

    .checkboxesSegmentStyle {
        ${mt(3)};
    }

    @media (max-width: ${theme.breakpoints.md}) {
        display: none;
    }
`;

const resultsWrapper = css`
    ${flex()};
    ${flexDirection("column")};
    ${mb(5)};
    gap: ${calculateRemSize(2)};
`;

const bottomButton = css`
    align-self: center;
    max-height: ${calculateRemSize(5)};
    ${mb(1)};
`;

const headerStyle = css`
    ${mt(5)};
    ${mb(4)};
`;

const HeaderFilterWrapper = styled.div<{reverseDirection: boolean}>`
    ${flex("flex-end", "space-between")};
    flex-direction: column;
    ${mt(4)};
    gap: ${calculateRemSize(4)};

    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        flex-direction: ${(props) => (props.reverseDirection ? "row-reverse" : "row")};
    }
`;

const inputWrapper = (theme: Theme) => css`
    @media (max-width: ${theme.breakpoints.md}) {
        display: none;
    }
`;

const backToLocationButton = css`
    align-self: center;
    ${mt(3)};
`;

const modalStyle = css`
    @media (min-width: ${getThemeBreakpoint().sm}) {
        min-width: 360px;
        max-height: 500px;
    }
`;
import {Fragment, useEffect, useMemo, useState} from "react";
import {connect} from "react-redux";
import {css, Theme} from "@emotion/react";
import styled from "@emotion/styled";
import {bindActionCreators, Dispatch} from "redux";

import {Button} from "@pg-design/button-module";
import {WideContent} from "@pg-design/grid-module";
import {backgroundColor, calculateRemSize, flex, flexDirection, mb, mt, p, pb, ph, pv, w100} from "@pg-design/helpers-css";
import {ArrowIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, FiltersIcon} from "@pg-design/icons-module";
import {InputWrapper} from "@pg-design/inputs-module";
import {Loader} from "@pg-design/loader-module";
import {PaginationSize, PaginationWithList} from "@pg-design/pagination";
import {getThemeBreakpoint} from "@pg-design/styles-strings";
import {Text} from "@pg-design/text-module";
import {FormActions} from "@pg-mono/form";
import {uniqBy} from "@pg-mono/nodash";
import {useUserDevice} from "@pg-mono/user-device";

import {scrollToTop} from "../../app/components/ScrollToTop";
import {IRPStore} from "../../app/rp_reducer";
import {ListingTypeTabs} from "../../atoms/listing_type_tabs/ListingTypeTabs";
import {MobileOptionsModal} from "../../atoms/MobileOptionsModal";
import {notifyBugsnag} from "../../errors/bugsnag/notify_bugsnag";
import {ErrorView} from "../../errors/views/ErrorView";
import {LeadModal} from "../../lead/components/lead_modal/LeadModal";
import {sortWrapper} from "../../offer/detail/components/property_list/OfferDetailPropertyListFilters";
import {Sort} from "../../offer/detail/components/sort/Sort";
import {SortOptions} from "../../offer/detail/components/sort/SortOptions";
import {sortOptions} from "../../offer/list/components/OfferListSort";
import {OfferType} from "../../offer/types/OfferType";
import {propertyListSortOptions} from "../../property/constants/property_list_sort_options";
import {IPropertyListProperty} from "../../property/types/PropertyListProperty";
import {fetchAllSearchLists, IFetchAllSearchListsParams, SearchTab} from "../../search/actions/fetch_search_all_action";
import {ListingType} from "../../types/ListingType";
import {IValueLabel} from "../../variables/types/IValueLabel";
import {GTMEvent, propertyConfiguratorAnalytics} from "../analytics/events";
import {hitPropertyConfiguratorSearch} from "../analytics/hit_property_configurator_search";
import {fetchAllPropertiesCount} from "../api/fetch_all_properties_count";
import {fetchAllPropertiesFromOffer} from "../api/fetch_all_properties_from_offer";
import {fetchFirstProperties} from "../api/fetch_first_properties";
import {FiltersForm} from "../components/filters/FiltersForm";
import {MobileFiltersModal} from "../components/filters/MobileFiltersModal";
import {HorizontalPropertyBox} from "../components/HorizontalPropertyBox";
import {Map} from "../components/Map";
import {OfferHeader} from "../components/OfferHeader";
import {usePropertyConfiguratorFiltersAnalytics} from "../hooks/use_property_configurator_filters_analytics";
import {searchFormFields} from "../reducers/property_configurator_reducer";
import {IPropertyConfiguratorProperty} from "../types/IPropertyConfiguratorProperty";
import {getParams} from "../utils/get_property_api_params";
import {getValuesWithCurrentSearchFieldState} from "../utils/get_values_with_current_search_field_state";
import {handleFormChange} from "../utils/handle_form_change";
import {getPropertyConfiguratorStorageData} from "../utils/property_configurator_storage";
import {searchFormActions, SearchFormValuesType} from "./PropertyConfigurator";

interface IStateProps {
    formValues: SearchFormValuesType;
    propertyQuarters: IValueLabel[] | undefined;
}
interface IActionsProps {
    formActions: FormActions<Record<keyof typeof searchFormFields, unknown>>;
    fetchAllSearchLists: (searchInput: string, params: IFetchAllSearchListsParams) => Promise<SearchTab>;
}

interface IOwnProps {
    handleStoreValues: (values: SearchFormValuesType) => void;
}

interface IProps extends IStateProps, IActionsProps, IOwnProps {}

export const ResultsListC = (props: IProps) => {
    const [isDropdownOpen, setDropdownOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [currentOfferPage, setCurrentOfferPage] = useState(1);
    const [maxPage, setMaxPage] = useState(1);
    const [maxOfferPage, setMaxOfferPage] = useState(1);
    const {isMobile} = useUserDevice();
    const [isLoading, setIsLoading] = useState(false);
    const [fetchingError, setFetchingError] = useState(false);
    const [firstProperties, setFirstProperties] = useState<IPropertyConfiguratorProperty[]>([]);
    const [allPropertiesCount, setAllPropertiesCount] = useState(0);
    const [propertiesFromOffer, setPropertiesFromOffer] = useState<IPropertyConfiguratorProperty[]>([]);
    const [propertiesInOfferCount, setPropertiesInOfferCount] = useState(0);
    const [sortValue, setSortValue] = useState(1);
    const [temporarySortValue, setTemporarySortValue] = useState(0);
    const [propertySort, setPropertySort] = useState(0);
    const [isMobileSortActive, setMobileSortActive] = useState(false);
    const [firstPropertiesParamsJSON, setFirstPropertiesParamsJSON] = useState("");
    const [filtersModalOpen, setFiltersModalOpen] = useState(false);
    const [_, setShouldRerender] = useState(0);

    const {trackArea, trackFloors, trackRooms, trackWorldDirections, trackAdditionalAreas, trackKitchenType} = usePropertyConfiguratorFiltersAnalytics(
        GTMEvent.FLAT_CONFIG_SIDEBAR
    );

    const filterProps = {
        values: props.formValues,
        onChange: (value: Partial<SearchFormValuesType>) => {
            handleFormChange(value, props.formActions.update, props.formValues);

            trackRooms(value);
        }
    };

    const [expandedOffer, setExpandedOffer] = useState<null | IPropertyListProperty>(null);

    const propertiesWithUniqueSlugs = uniqBy(firstProperties, (item) => item.offer.slug);
    const sortOptionsVariant = useMemo(
        () => (expandedOffer ? propertyListSortOptions : sortOptions.map((option) => ({label: option.title, value: parseInt(option.value)}))),
        [expandedOffer]
    );

    const getFirstProperties = async (page: number, name?: string, value?: unknown) => {
        try {
            const values = {
                ...props.formValues,
                ...(name === "search" && {search: value}),
                ...(name === "price" && {price: value}),
                ...(name === "construction_end_date" && {construction_end_date: value})
            };
            const params = getParams(values, sortValue);
            const paramsWithPage = {...params, page};
            if (JSON.stringify(paramsWithPage) === firstPropertiesParamsJSON) {
                return;
            }
            scrollToTop();
            setFetchingError(false);
            setIsLoading(true);
            setFirstPropertiesParamsJSON(JSON.stringify(paramsWithPage));
            const data = await fetchFirstProperties(paramsWithPage);
            setFirstProperties(data.results);
            setMaxPage(Math.ceil(data.count / data.page_size));
            const newCount = await fetchAllPropertiesCount(params);
            hitPropertyConfiguratorSearch(values, data);
            setAllPropertiesCount(newCount);
            setIsLoading(false);
        } catch (error) {
            const message = "Error in fetchFirstProperties or fetchFirstPropertiesFlatsForSaleCount";
            setIsLoading(false);
            setFetchingError(true);
            notifyBugsnag(error, error instanceof Error ? error.message : message);
        }
    };

    const getPropertiesFromOffer = async (page: number, ignoreSort?: boolean) => {
        try {
            if (!expandedOffer) {
                return;
            }
            setFetchingError(false);
            setIsLoading(true);
            const propertySortedBy = (expandedOffer && propertyListSortOptions.find((option) => option.value === propertySort)?.slug) || (ignoreSort && "");
            const params = {
                ...getParams(getPropertyConfiguratorStorageData(), sortValue),
                page,
                ...(propertySortedBy ? {sort: propertySortedBy} : {})
            };
            const data = await fetchAllPropertiesFromOffer(params, expandedOffer.offer.id);
            setMaxOfferPage(Math.ceil(data.count / data.page_size));
            setPropertiesFromOffer(data.results);
            setPropertiesInOfferCount(data.count);
            setIsLoading(false);
            scrollToTop();
        } catch (error) {
            setIsLoading(false);
            setFetchingError(true);
        }
    };

    const handleOfferClick = (offerId: string | number) => {
        setIsLoading(true);
        setPropertiesFromOffer([]);
        const propertySortedBy = expandedOffer && propertyListSortOptions.find((option) => option.value === propertySort)?.slug;
        const params = {...getParams(props.formValues, sortValue), page: 1, ...(propertySortedBy ? {sort: propertySortedBy} : {})};
        const indexOfMatchingProperty = firstProperties.findIndex(({offer}) => offer.id === offerId);
        if (indexOfMatchingProperty >= 0) {
            setExpandedOffer(firstProperties[indexOfMatchingProperty]);
        }
        fetchAllPropertiesFromOffer(params, offerId).then((data) => {
            setCurrentOfferPage(1);
            setPropertiesFromOffer(data.results);
            setIsLoading(false);
            setPropertiesInOfferCount(data.count);
        });
    };

    const discardModal = () => {
        if (sortValue !== 0) {
            setSortValue(sortValue);
        }
        setMobileSortActive(false);
    };

    const clearSort = () => {
        if (expandedOffer) {
            setPropertySort(0);
            setTemporarySortValue(0);
        }
        if (!expandedOffer) {
            setSortValue(1);
            setTemporarySortValue(1);
        }
        if (isMobile) {
            setMobileSortActive(false);
        }
        setDropdownOpen(false);
    };

    const saveSort = () => {
        if (isMobile) {
            setMobileSortActive(false);
        }
        if (expandedOffer) {
            setPropertySort(temporarySortValue);
        } else {
            setSortValue(temporarySortValue);
        }
        setDropdownOpen(false);
    };

    useEffect(() => {
        setTimeout(() => getFirstProperties(currentPage), 10);
    }, [currentPage, sortValue]);

    useEffect(() => {
        getPropertiesFromOffer(currentOfferPage);
    }, [currentOfferPage, propertySort]);

    useEffect(() => {
        if (!expandedOffer) {
            setTemporarySortValue(sortValue);
            setMaxOfferPage(1);
            setCurrentOfferPage(1);
        }
        if (expandedOffer) {
            setTemporarySortValue(propertySort);
            getPropertiesFromOffer(1);
            setPropertiesFromOffer([]);
        }
    }, [expandedOffer]);

    useEffect(() => {
        if (!expandedOffer && sortValue !== temporarySortValue) {
            setTemporarySortValue(sortValue);
        }
        if (expandedOffer && propertySort !== temporarySortValue) {
            setTemporarySortValue(propertySort);
        }
    }, [isDropdownOpen]);

    useEffect(() => {
        //This has to be here in order to correctly render React-range's thumbs. Otherwise it tries to calculate thumbs' position when animation is started resulting in bad position. If we wait for modal to finish opening, then it's fine
        setTimeout(() => setShouldRerender((prev) => prev + 1), 250);
    }, [filtersModalOpen]);

    if (fetchingError) {
        return <ErrorView />;
    }

    return (
        <>
            <WideContent css={wrapper}>
                <div css={preferencesSidebar}>
                    <Text variant="headline_3">Twoje preferencje</Text>

                    <FiltersForm {...filterProps} />

                    <Button
                        disabled={isLoading}
                        size="big"
                        variant="filled_primary"
                        onClick={() => {
                            if (expandedOffer) {
                                setExpandedOffer(null);
                            }
                            if (currentPage !== 1) {
                                setCurrentPage(1);
                            } else {
                                getFirstProperties(1);
                            }
                            props.handleStoreValues(props.formValues);
                            propertyConfiguratorAnalytics.gtm.propertyConfiguratorEvent(GTMEvent.FLAT_CONFIG_CHANGE);
                            trackArea();
                            trackFloors();
                            trackWorldDirections();
                            trackAdditionalAreas();
                            trackKitchenType();
                        }}
                    >
                        Zapisz
                    </Button>
                </div>
                <div css={[flex(), flexDirection("column"), w100, pb(4)]}>
                    {expandedOffer ? (
                        <OfferHeader
                            handleBackClick={() => {
                                propertyConfiguratorAnalytics.gtm.propertyConfiguratorEvent(GTMEvent.FLAT_CONFIG_BACK);
                                setExpandedOffer(null);
                            }}
                            images={expandedOffer.offer?.main_image}
                            name={expandedOffer.offer.name}
                            address={expandedOffer.offer.address}
                            vendorLogo={expandedOffer.offer.vendor.logo?.v_log_160x120}
                            onChange={props.formActions.update}
                            values={props.formValues}
                            formActions={props.formActions}
                            onEditClick={setFiltersModalOpen}
                        />
                    ) : (
                        <Map
                            fetchAllSearchLists={props.fetchAllSearchLists}
                            onChange={props.formActions.update}
                            values={props.formValues}
                            formActions={props.formActions}
                            handleChange={(name, value) => {
                                propertyConfiguratorAnalytics.gtm.propertyConfiguratorEvent(GTMEvent.FLAT_CONFIG_CHANGE);
                                setCurrentPage(1);
                                getFirstProperties(1, name, value);
                                const values = getValuesWithCurrentSearchFieldState(props.formValues, name, value);
                                props.handleStoreValues(values);
                            }}
                            properties={firstProperties}
                            handleOfferClick={handleOfferClick}
                            onEditClick={setFiltersModalOpen}
                        />
                    )}
                    {isLoading && <Loader size="md" css={p(12.5)} />}
                    {!isLoading && (
                        <>
                            <div css={headerStyle}>
                                <Text variant="headline_4">
                                    {expandedOffer
                                        ? `Dostępne mieszkania w tej inwestycji (${propertiesInOfferCount})`
                                        : `Dostępne mieszkania w tej lokalizacji (${allPropertiesCount || 0})`}
                                </Text>
                                <HeaderFilterWrapper reverseDirection={!!expandedOffer}>
                                    {!expandedOffer && (
                                        <ListingTypeTabs
                                            filters={props.formValues}
                                            additionalParams={{sort: temporarySortValue}}
                                            currentListingType={ListingType.PROPERTIES}
                                            offerType={OfferType.FLAT}
                                        />
                                    )}
                                    <InputWrapper css={[sortWrapper, inputWrapper]}>
                                        <Sort
                                            isDropdownOpen={isDropdownOpen}
                                            setDropdownOpen={setDropdownOpen}
                                            isMobile={isMobile as boolean}
                                            setDesktopFilterBlendActive={() => null}
                                            currentValue={temporarySortValue}
                                            setCurrentValue={setTemporarySortValue}
                                            sortOptions={sortOptionsVariant}
                                            rightDropdown
                                            dropdownShadow
                                            disableClearAction
                                            closeOnOutsideClick
                                        >
                                            <SortOptions
                                                currentValue={temporarySortValue}
                                                setCurrentValue={setTemporarySortValue}
                                                sortOptions={sortOptionsVariant}
                                                onCancel={clearSort}
                                                onSave={saveSort}
                                            />
                                        </Sort>
                                    </InputWrapper>
                                </HeaderFilterWrapper>
                                {isMobile && (
                                    <>
                                        <Button
                                            css={[w100, mt(4)]}
                                            variant="outlined_secondary"
                                            iconLeft={FiltersIcon}
                                            onClick={() => setMobileSortActive((prev) => !prev)}
                                        >
                                            Sortuj
                                        </Button>
                                        <MobileOptionsModal modalHeading="Sortuj" isOpen={isMobileSortActive} onModalClose={discardModal} css={modalStyle}>
                                            <SortOptions
                                                currentValue={temporarySortValue}
                                                setCurrentValue={setTemporarySortValue}
                                                sortOptions={sortOptionsVariant}
                                                onCancel={clearSort}
                                                onSave={saveSort}
                                            />
                                        </MobileOptionsModal>
                                    </>
                                )}
                            </div>
                            <div css={resultsWrapper}>
                                {!expandedOffer &&
                                    propertiesWithUniqueSlugs.map((property, index) => (
                                        <Fragment key={index}>
                                            <HorizontalPropertyBox property={property} />
                                            {property.matching_properties_count > 1 && (
                                                <Button
                                                    variant="none_secondary"
                                                    onClick={() => {
                                                        setPropertiesFromOffer([]);
                                                        setExpandedOffer(property);
                                                        propertyConfiguratorAnalytics.gtm.propertyConfiguratorEvent(GTMEvent.FLAT_CONFIG_MORE);
                                                    }}
                                                    iconLeft={ChevronDownIcon}
                                                    css={bottomButton}
                                                >
                                                    Pokaż jeszcze {property.matching_properties_count - 1} z tej inwestycji
                                                </Button>
                                            )}
                                        </Fragment>
                                    ))}
                                {expandedOffer && propertiesFromOffer.map((property, index) => <HorizontalPropertyBox property={property} key={index} />)}
                            </div>
                            <PaginationWithList
                                iconPrev={<ChevronLeftIcon />}
                                iconNext={<ChevronRightIcon />}
                                pageCount={expandedOffer ? maxOfferPage : maxPage}
                                currentPage={expandedOffer ? currentOfferPage : currentPage}
                                multiNumbersInside
                                size={isMobile ? PaginationSize.SM : PaginationSize.LG}
                                onChangePageClick={expandedOffer ? setCurrentOfferPage : setCurrentPage}
                            />
                            {expandedOffer && (
                                <Button
                                    variant="outlined_secondary"
                                    iconLeft={ArrowIcon}
                                    onClick={() => {
                                        scrollToTop();
                                        propertyConfiguratorAnalytics.gtm.propertyConfiguratorEvent(GTMEvent.FLAT_CONFIG_BACK);
                                        setExpandedOffer(null);
                                    }}
                                    css={backToLocationButton}
                                >
                                    Powrót do lokalizacji
                                </Button>
                            )}
                        </>
                    )}
                </div>
                <LeadModal />
                <MobileFiltersModal
                    isOpen={filtersModalOpen}
                    onClose={setFiltersModalOpen}
                    handleChange={(name, value) => {
                        propertyConfiguratorAnalytics.gtm.propertyConfiguratorEvent(GTMEvent.FLAT_CONFIG_CHANGE);
                        setCurrentPage(1);
                        getFirstProperties(1, name, value);
                        const values = getValuesWithCurrentSearchFieldState(props.formValues, name, value);
                        props.handleStoreValues(values);
                    }}
                    {...filterProps}
                />
            </WideContent>
        </>
    );
};

export const ResultsList = connect(mapStateToProps, mapActionsToProps)(ResultsListC);

function mapActionsToProps(dispatch: Dispatch): IActionsProps {
    // fetch search lists for property configurator
    const searchContentParams = {
        googlePOIs: false,
        regions: true,
        offers: false,
        vendors: false
    };
    return {
        formActions: bindActionCreators({...searchFormActions}, dispatch),
        ...bindActionCreators({fetchAllSearchLists: (searchInput: string) => fetchAllSearchLists(searchInput, {searchContent: searchContentParams})}, dispatch)
    };
}

function mapStateToProps(state: IRPStore): IStateProps {
    return {
        formValues: state.propertyConfigurator.formValues,
        propertyQuarters: state.variables.data?.property_quarters
    };
}

const wrapper = css`
    ${flex()};
    gap: ${calculateRemSize(3)};
`;

const preferencesSidebar = (theme: Theme) => css`
    ${flex()};
    ${flexDirection("column")};
    width: 100%;
    max-width: 384px;
    ${backgroundColor("white")};
    ${ph(3)};
    ${pv(4)};
    gap: ${calculateRemSize(4)};

    .checkboxesSegmentStyle {
        ${mt(3)};
    }

    @media (max-width: ${theme.breakpoints.md}) {
        display: none;
    }
`;

const resultsWrapper = css`
    ${flex()};
    ${flexDirection("column")};
    ${mb(5)};
    gap: ${calculateRemSize(2)};
`;

const bottomButton = css`
    align-self: center;
    max-height: ${calculateRemSize(5)};
    ${mb(1)};
`;

const headerStyle = css`
    ${mt(5)};
    ${mb(4)};
`;

const HeaderFilterWrapper = styled.div<{reverseDirection: boolean}>`
    ${flex("flex-end", "space-between")};
    flex-direction: column;
    ${mt(4)};
    gap: ${calculateRemSize(4)};

    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        flex-direction: ${(props) => (props.reverseDirection ? "row-reverse" : "row")};
    }
`;

const inputWrapper = (theme: Theme) => css`
    @media (max-width: ${theme.breakpoints.md}) {
        display: none;
    }
`;

const backToLocationButton = css`
    align-self: center;
    ${mt(3)};
`;

const modalStyle = css`
    @media (min-width: ${getThemeBreakpoint().sm}) {
        min-width: 360px;
        max-height: 500px;
    }
`;
